import { Form } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { brandSettingsProgressionXpRulesPath } from "@/routes"
import type { XpRules, XpRulesRanges } from "@/types/progression"

/**
 * OKS-52 — Card B on the Progression page: XP-per-event rules.
 *
 * Four labeled number inputs (PRD §1 — base / on-time bonus /
 * engagement bonus / engagement view threshold). Mirrors the OKS-40
 * `<BrandSettingsThresholds>` page's per-field number-input pattern.
 *
 * Errors come back keyed by snake_case field name (`xp_base_per_mission`,
 * `xp_on_time_bonus`, etc.) — `BrandSettingsXpRulesController`
 * accumulates per-field `ArgumentError`s from the model writers.
 */
export function XpRulesForm({
  xpRules,
  ranges,
  canEdit,
}: {
  xpRules: XpRules
  ranges: XpRulesRanges
  canEdit: boolean
}) {
  const { t } = useTranslation()

  return (
    <Card data-testid="brand-settings-progression-xp-rules-card">
      <CardHeader>
        <HeadingSmall
          title={t("settings.brand.progression.xp_rules.title")}
          description={t("settings.brand.progression.xp_rules.description")}
        />
      </CardHeader>
      <CardContent>
        <Form
          method="patch"
          action={brandSettingsProgressionXpRulesPath()}
          options={{ preserveScroll: true }}
          className="space-y-6"
          data-testid="brand-settings-progression-xp-rules-form"
        >
          {({ errors, processing }) => {
            const errs = errors as Record<string, string | string[]>

            return (
              <>
                {/* t() calls are LITERAL per field so the
                    inertia_i18n_health spec's static-key scanner sees
                    every key as used. Resolved strings are passed
                    into `<NumberField>` rather than key paths. Short
                    testid aliases (`xp-base-input`, `xp-on-time-input`
                    etc.) match the QA Plan contract used by
                    Playwright. */}
                <NumberField
                  field="xp_base_per_mission"
                  testid="brand-settings-progression-xp-base-input"
                  label={t("settings.brand.progression.xp_rules.base_label")}
                  helper={t("settings.brand.progression.xp_rules.base_helper", {
                    min: ranges.xp_base_per_mission.min,
                    max: ranges.xp_base_per_mission.max,
                  })}
                  value={xpRules.xp_base_per_mission}
                  errors={errs}
                  canEdit={canEdit}
                />
                <NumberField
                  field="xp_on_time_bonus"
                  testid="brand-settings-progression-xp-on-time-input"
                  label={t("settings.brand.progression.xp_rules.on_time_label")}
                  helper={t(
                    "settings.brand.progression.xp_rules.on_time_helper",
                    {
                      min: ranges.xp_on_time_bonus.min,
                      max: ranges.xp_on_time_bonus.max,
                    },
                  )}
                  value={xpRules.xp_on_time_bonus}
                  errors={errs}
                  canEdit={canEdit}
                />
                <NumberField
                  field="xp_engagement_bonus"
                  testid="brand-settings-progression-xp-engagement-input"
                  label={t(
                    "settings.brand.progression.xp_rules.engagement_label",
                  )}
                  helper={t(
                    "settings.brand.progression.xp_rules.engagement_helper",
                    {
                      min: ranges.xp_engagement_bonus.min,
                      max: ranges.xp_engagement_bonus.max,
                    },
                  )}
                  value={xpRules.xp_engagement_bonus}
                  errors={errs}
                  canEdit={canEdit}
                />
                <NumberField
                  field="engagement_threshold_views"
                  testid="brand-settings-progression-xp-engagement-views-input"
                  label={t(
                    "settings.brand.progression.xp_rules.engagement_views_label",
                  )}
                  helper={t(
                    "settings.brand.progression.xp_rules.engagement_views_helper",
                    {
                      min: ranges.engagement_threshold_views.min,
                      max: ranges.engagement_threshold_views.max,
                    },
                  )}
                  value={xpRules.engagement_threshold_views}
                  errors={errs}
                  canEdit={canEdit}
                />

                {canEdit ? (
                  <Button
                    type="submit"
                    disabled={processing}
                    data-testid="brand-settings-progression-xp-rules-save"
                    className="min-h-11 w-full sm:w-auto"
                  >
                    {processing
                      ? t("settings.brand.progression.xp_rules.saving")
                      : t("settings.brand.progression.xp_rules.save")}
                  </Button>
                ) : (
                  <p
                    role="status"
                    data-testid="brand-settings-progression-xp-rules-readonly"
                    className="text-muted-foreground text-sm"
                  >
                    {t("settings.brand.progression.read_only_hint")}
                  </p>
                )}
              </>
            )
          }}
        </Form>
      </CardContent>
    </Card>
  )
}

function NumberField({
  field,
  testid,
  label,
  helper,
  value,
  errors,
  canEdit,
}: {
  field: string
  testid: string
  label: string
  helper: string
  value: number
  errors: Record<string, string | string[]>
  canEdit: boolean
}) {
  const helperId = `${field}-helper`
  const rawError = errors[field]
  const messages =
    rawError == null
      ? undefined
      : Array.isArray(rawError)
        ? rawError
        : [rawError]

  return (
    <div className="grid gap-2">
      <Label htmlFor={field}>{label}</Label>
      {/* No `min`/`max` HTML5 attrs — defence-in-depth on the
          server-side `ArgumentError` is the bounds floor (the
          QA-Plan-OKS-52 cross-card test fills 99999 and expects
          the server to surface `aria-invalid`; an HTML5 max
          would short-circuit submission before the server saw it).
          Helper text + `range` prop carry the bounds copy. */}
      <Input
        id={field}
        type="number"
        name={`brand[${field}]`}
        step={1}
        inputMode="numeric"
        defaultValue={value}
        readOnly={!canEdit}
        aria-readonly={!canEdit}
        aria-describedby={helperId}
        aria-invalid={messages ? true : undefined}
        data-testid={testid}
        className="max-w-[200px]"
      />
      <p id={helperId} className="text-muted-foreground text-sm">
        {helper}
      </p>
      <InputError messages={messages} />
    </div>
  )
}
